import React, { useState, useEffect } from 'react';
import { UserInvestments } from '../components/round/user-investments';
import { UserDashboardComponent } from '../components/dashboard/user-dashboard.component';
import { UserStakingContainer } from '../components/user/user-staking-container';
import { ClaimFromSnapshotComponent } from '../components/dashboard/claim-from-snapshot';

// Icons from lucide-react
import { 
  LayoutDashboard, 
  Coins, 
  History, 
  Download, 
  ChevronRight, 
  ChevronLeft
} from 'lucide-react';
import { useTheme } from '../components/navbar/navbar';

type UserPageTabs = 'DASHBOARD' | 'STAKING' | 'USER_INVESTMENTS' | 'CLAIM';

// Tab configuration with icons and labels
const UserPageTabItems = [
  {
    label: 'Sale Rounds',
    identifier: 'DASHBOARD',
    icon: LayoutDashboard,
    description: 'Overview of your portfolio and activity'
  },
  {
    label: 'Staking',
    identifier: 'STAKING',
    icon: Coins,
    description: 'Manage your staked assets'
  },
  // {
  //   label: 'My Investments',
  //   identifier: 'USER_INVESTMENTS',
  //   icon: History,
  //   description: 'View your previous investment history'
  // },
  {
    label: 'Claim',
    identifier: 'CLAIM',
    icon: Download,
    description: 'Claim your rewards and earnings'
  },
];

export const UserPage = () => {
  const [selectedTab, setSelectedTab] = useState('DASHBOARD');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [animationClass, setAnimationClass] = useState('fade-in');
  
  const { isDarkMode } = useTheme();
  
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  
  const handleTabChange = (tab: any) => {
    setAnimationClass('slide-out');
    
    setTimeout(() => {
      setSelectedTab(tab);
      setAnimationClass('slide-in');
    }, 150);
  };
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationClass('');
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  const getContent = () => {
    switch(selectedTab) {
      case 'DASHBOARD':
        return <UserDashboardComponent />;
      case 'STAKING':
        return <UserStakingContainer />;
      // case 'USER_INVESTMENTS':
      //   return <UserInvestments />;
      case 'CLAIM':
        return <ClaimFromSnapshotComponent />;
      default:
        return <UserDashboardComponent />;
    }
  };
  
  return (
    <div className={`flex flex-col h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-800'} transition-colors duration-300`}>
      
      {/* Main content area with sidebar */}
      <div className="flex flex-1 overflow-hidden pt-20"> {/* Added pt-20 to account for navbar height */}
        {/* Sidebar positioned below navbar */}
        <div 
          className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} ${
            sidebarCollapsed ? 'w-20' : 'w-64'
          } h-full flex-shrink-0 border-r ${
            isDarkMode ? 'border-gray-700' : 'border-gray-200'
          } transition-all duration-300 ease-in-out flex flex-col fixed z-20 mt-10`} /* Added mt-20 */
        >      
          {/* Logo area */}
          <div className="p-4 flex items-center justify-between">
            <button 
              onClick={toggleSidebar}
              className={`${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} p-1 rounded-full transition-colors`}
            >
              {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          </div>
          
          {/* Navigation items */}
          <div className="flex-1 overflow-y-auto py-4">
            <ul className="space-y-2 px-3">
              {UserPageTabItems.map((item) => {
                const Icon = item.icon;
                const isActive = selectedTab === item.identifier;
                
                return (
                  <li key={item.identifier}>
                    <button
                      onClick={() => handleTabChange(item.identifier)}
                      className={`flex items-center ${
                        sidebarCollapsed ? 'justify-center' : 'justify-between'
                      } w-full p-3 rounded-lg transition-all duration-200 ${
                        isActive 
                          ? 'bg-orange-400 text-white'
                          : `${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`
                      }`}
                    >
                      <div className="flex items-center">
                        <Icon size={20} />
                        {!sidebarCollapsed && (
                          <span className="ml-3 font-medium">{item.label}</span>
                        )}
                      </div>
                      {!sidebarCollapsed && isActive && (
                        <ChevronRight size={16} />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
        
        {/* Main content area */}
        <div 
          className="flex-1 flex flex-col overflow-hidden" 
          style={{ 
            marginLeft: sidebarCollapsed ? '5rem' : '16rem',
            paddingTop: '1rem' 
          }}
        >        
          <main className="flex-1 overflow-auto p-6">
            <div className={animationClass}>
              <div className={`${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              } rounded-xl shadow-sm p-6 border`}>
                {getContent()}
              </div>
            </div>
          </main>
        </div>
      </div>
      
      {/* CSS for animations */}
      <style>{`
        .fade-in {
          animation: fadeIn 0.5s ease-out;
        }
        
        .slide-in {
          animation: slideIn 0.3s ease-out;
        }
        
        .slide-out {
          animation: slideOut 0.2s ease-out;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(10px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes slideOut {
          from { opacity: 1; transform: translateX(0); }
          to { opacity: 0; transform: translateX(-10px); }
        }
      `}</style>
    </div>
  );
};

export default UserPage;
